import { FC, useState } from 'react'

import styled from 'styled-components'

import { getAuthEmail } from 'src/Redux/auth-process/authTypeSlice/selectors'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { sendQuickLoginLink } from 'src/Redux/auth-process/emailPasswordSlice/api-actions'
import { useQuickLoginUTMParameters } from 'src/Hooks/useQuickLoginUTMParameters'
import { quickLogin } from 'src/Helpers/TagManager/AuthEvents'
import { GoogleAuthButton } from 'src/Components/Buttons/GoogleAuthButton'
import { TextLinkButton } from 'src/Components/Buttons/TextLinkButton'
import { OrDivider } from 'src/Components/OrDivider'
import { initiateGoogleAuth } from 'src/Utils/googleAuth'
import { maskEmail } from 'src/Utils/maskEmail'
import { useGoogleAuthError } from 'src/Hooks/useGoogleAuthError'
import GreenCheckMark from 'src/Assets/Svg/checkmark-green-round.svg?react'
import { variables } from 'src/Styled/variables'

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  width: 100%;
  text-align: center;
`

const Title = styled.p`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;

  @media (min-width: 576px) {
    font-size: 28px;
  }
`

const Subtitle = styled.p`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

const ResendBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  min-height: 48px;
  width: 100%;
`

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  color: ${({ theme }) => theme.color.brandGreen};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 2px;
  }
`

const GreenTextLinkButton = styled(TextLinkButton)`
  color: ${({ theme }) => theme.color.brandGreen};
`

const GoogleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ErrorRow = styled.div`
  color: ${({ theme }) => theme.color.mediumRed};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

export const LoginWithoutSignup: FC = () => {
  const dispatch = useAppDispatch()
  const email = useAppSelector(getAuthEmail)
  const utmParameters = useQuickLoginUTMParameters()
  const googleAuthError = useGoogleAuthError()
  const [resent, setResent] = useState(false)

  const handleResend = (): void => {
    void dispatch(sendQuickLoginLink({ email, utmParameters })).then(
      (action) => {
        if (sendQuickLoginLink.fulfilled.match(action)) {
          quickLogin()
          setResent(true)
        }
      },
    )
  }

  return (
    <>
      <Headline>
        <Title>Check your email</Title>
        <Subtitle>
          {`We've sent you a magic login link to your inbox. Please check ${maskEmail(email)}`}
        </Subtitle>
      </Headline>
      <ResendBlock>
        {resent ? (
          <SuccessMessage>
            <GreenCheckMark />
            <span>
              {`We've sent a new magic login link. Please check your inbox and spam folder.`}
            </span>
          </SuccessMessage>
        ) : (
          <GreenTextLinkButton type='button' onClick={handleResend}>
            Resend magic login link
          </GreenTextLinkButton>
        )}
      </ResendBlock>
      <OrDivider />
      <GoogleBlock>
        <GoogleAuthButton
          label='Log in with Google'
          onClick={() => initiateGoogleAuth({})}
        />
        {googleAuthError && <ErrorRow>{googleAuthError}</ErrorRow>}
      </GoogleBlock>
    </>
  )
}
